/**
 * WASM module loader with singleton pattern.
 * Ensures the WASM module is initialized only once.
 */

import type {
  WasmConvertOptions,
  WasmConvertResult,
  WasmValidationResult
} from './types';

// WASM module interface (generated by wasm-pack)
interface WasmModule {
  convert_cube_to_xmp: (options: WasmConvertOptions) => WasmConvertResult;
  validate_cube_file: (content: string) => WasmValidationResult;
}

let wasmModule: WasmModule | null = null;
let initPromise: Promise<void> | null = null;

/**
 * Initialize the WASM module. Safe to call multiple times.
 * Returns immediately if already initialized.
 */
export async function initWasm(): Promise<void> {
  if (wasmModule) {
    return;
  }

  if (initPromise) {
    return initPromise;
  }

  initPromise = (async () => {
    try {
      // Keep the import path static so Vite includes both the JS wrapper and .wasm asset in production builds.
      const wasm = await import('./pkg/lightroom_lut_converter.js');
      await wasm.default();

      wasmModule = {
        convert_cube_to_xmp: wasm.convert_cube_to_xmp,
        validate_cube_file: wasm.validate_cube_file,
      };
    } catch {
      initPromise = null;
      throw new Error(
        'Failed to initialize WASM module. Run `npm run build:wasm` after installing `wasm-pack`.',
      );
    }
  })();

  return initPromise;
}

/**
 * Check if the WASM module is ready.
 */
export function isWasmReady(): boolean {
  return wasmModule !== null;
}

/**
 * Get the WASM module. Throws if not initialized.
 */
function getWasm(): WasmModule {
  if (!wasmModule) {
    throw new Error('WASM module not initialized. Call initWasm() first.');
  }
  return wasmModule;
}

/**
 * Convert a CUBE file to XMP format.
 *
 * @param options - Conversion options including CUBE content
 * @returns Conversion result (success with XMP content or error)
 */
export function convertCubeToXmp(options: WasmConvertOptions): WasmConvertResult {
  return getWasm().convert_cube_to_xmp(options);
}

/**
 * Validate a CUBE file without performing full conversion.
 *
 * @param content - CUBE file content as UTF-8 string
 * @returns Validation result (valid with metadata or invalid with error)
 */
export function validateCubeFile(content: string): WasmValidationResult {
  return getWasm().validate_cube_file(content);
}

// Re-export types for convenience
export type {
  WasmConvertOptions,
  WasmConvertResult,
  WasmValidationResult,
  WasmWarning,
  WasmConvertSuccess,
  WasmConvertError,
  WasmValidationValid,
  WasmValidationInvalid,
} from './types';

export {
  DEFAULT_CONVERT_OPTIONS,
  isConvertSuccess,
  isConvertError,
  isValidationValid,
  isValidationInvalid,
} from './types';
